import React, { useState, useEffect } from 'react';
import { BPApi, BPReading } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { AIDialog } from './AIDialog';
import { analyzeReadings } from '../services/ai';

interface MainScreenProps {
  patientId: string;
  onLogout: () => void;
  api: BPApi;
}

const MOODS = [
  { value: 1, emoji: '😢' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '🙂' },
  { value: 5, emoji: '😊' }
];

export function MainScreen({ patientId, onLogout, api }: MainScreenProps) {
  const [readings, setReadings] = useState<BPReading[]>([]);
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [pulse, setPulse] = useState('');
  const [mood, setMood] = useState<number | undefined>();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [isAILoading, setIsAILoading] = useState(false);
  const [aiError, setAiError] = useState<string | undefined>();
  const { t, language } = useLanguage();

  useEffect(() => {
    loadReadings();
  }, []);

  const loadReadings = async () => {
    try {
      const data = await api.getReadings(patientId);
      setReadings(data);
    } catch (error) {
      console.error('Failed to load readings:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    const systolicNum = parseInt(systolic);
    const diastolicNum = parseInt(diastolic);
    const pulseNum = parseInt(pulse);

    if (!systolic || !diastolic || !pulse || !mood) {
      setError(t('main.error.required'));
      return;
    }

    if (systolicNum < 70 || systolicNum > 190) {
      setError(t('main.error.systolic'));
      return;
    }

    if (diastolicNum < 40 || diastolicNum > 100) {
      setError(t('main.error.diastolic'));
      return;
    }

    if (pulseNum < 40 || pulseNum > 200) {
      setError(t('main.error.pulse'));
      return;
    }

    setIsLoading(true);

    try {
      await api.addReading({
        patient_id: patientId,
        systolic: systolicNum,
        diastolic: diastolicNum,
        pulse: pulseNum,
        mood,
        comment: comment.trim() || undefined,
        date: new Date().toISOString()
      });

      // Reset form
      setSystolic('');
      setDiastolic('');
      setPulse('');
      setMood(undefined);
      setComment('');

      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Reload readings
      await loadReadings();
    } catch (error) {
      setError(t('main.error.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAskAI = async () => {
    if (readings.length === 0) {
      setAiError(t('ai.error.no.readings'));
      setIsAIDialogOpen(true);
      return;
    }

    setIsAILoading(true);
    setAiError(undefined);
    setIsAIDialogOpen(true);

    try {
      const response = await analyzeReadings(readings, language);
      setAiResponse(response);
    } catch (error) {
      setAiError(t('ai.error.failed'));
    } finally {
      setIsAILoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (moodValue?: number) => {
    return MOODS.find(m => m.value === moodValue)?.emoji || '😐';
  };

  return (
    <div className="container">
      <div className="card">
        <button className="logout-button" onClick={onLogout} title={t('main.logout')}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
          </svg>
        </button>

        <div className="header">
          <h1 className="title">{t('app.title')}</h1>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="input-group">
            <label htmlFor="systolic">{t('main.systolic')}</label>
            <input
              id="systolic"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="input"
              value={systolic}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setSystolic(value);
                }
              }}
              placeholder="120"
            />
          </div>

          <div className="input-group">
            <label htmlFor="diastolic">{t('main.diastolic')}</label>
            <input
              id="diastolic"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="input"
              value={diastolic}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setDiastolic(value);
                }
              }}
              placeholder="80"
            />
          </div>

          <div className="input-group">
            <label htmlFor="pulse">{t('main.pulse')}</label>
            <input
              id="pulse"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="input"
              value={pulse}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setPulse(value);
                }
              }}
              placeholder="72"
            />
          </div>

          <div className="input-group">
            <label htmlFor="mood">{t('main.mood')}</label>
            <div className="mood-selector">
              {MOODS.map(({ value, emoji }) => (
                <button
                  key={value}
                  type="button"
                  className={`mood-button ${mood === value ? 'selected' : ''}`}
                  onClick={() => setMood(value)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="comment">{t('main.comment')}</label>
            <input
              id="comment"
              type="text"
              className="input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('main.comment')}
            />
          </div>

          {error && <div className="error">{error}</div>}

          <button type="submit" className="button button-primary" disabled={isLoading}>
            {isLoading ? t('main.adding') : t('main.add')}
          </button>

          <button
            type="button"
            className="button button-outline"
            onClick={handleAskAI}
            disabled={isLoading}
          >
            {t('main.askAi')}
          </button>
        </form>

        {showSuccess && (
          <div className="success-message">
            Reading added successfully!
          </div>
        )}

        <div className="readings">
          <h2 className="readings-title">{t('main.readings')}</h2>
          {readings.length === 0 ? (
            <div className="loading">{t('main.no.readings')}</div>
          ) : (
            <div className="readings-grid">
              {readings.map((reading, index) => (
                <div key={index} className="reading-card">
                  <div className="reading-values">
                    <span className="reading-value">{reading.systolic}</span>
                    <span className="reading-unit">{t('main.unit.mmhg')}</span>
                    <span className="reading-value">{reading.diastolic}</span>
                    <span className="reading-unit">{t('main.unit.mmhg')}</span>
                    <span className="reading-pulse">
                      <span className="pulse-dot"></span>
                      {reading.pulse}
                      <span className="reading-unit">{t('main.unit.bpm')}</span>
                    </span>
                  </div>
                  <div className="reading-time">
                    <span>{formatDate(reading.date)}</span>
                    <span className="reading-mood">{getMoodEmoji(reading.mood)}</span>
                  </div>
                  {reading.comment && (
                    <div className="reading-comment">{reading.comment}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AIDialog
        isOpen={isAIDialogOpen}
        onClose={() => setIsAIDialogOpen(false)}
        content={aiResponse}
        isLoading={isAILoading}
        error={aiError}
      />
    </div>
  );
} 